import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

const ContributionSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const futureVisions = [
    'Technology serves human development',
    'Opportunity reaches underserved communities',
    'Innovation is shared, not hoarded',
    'Freedom of mind is respected',
    'Societies grow through knowledge, capability, and trust'
  ];

  return (
    <section 
      id="contribution" 
      className="py-24 md:py-32 relative" 
      ref={ref} 
      data-testid="contribution-section"
      style={{
        background: 'linear-gradient(180deg, #050505 0%, #0a1225 50%, #050505 100%)'
      }}
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-16 text-center"
        >
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-light tracking-[0.2em] text-white mb-4"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            Contribution
          </h2>
          <div className="w-24 h-[1px] bg-[#c9a227]/50 mx-auto" />
        </motion.div>

        {/* Opening Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-12 text-center"
        >
          <h3 
            className="text-xl md:text-2xl font-light tracking-wider text-[#c9a227] mb-6"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            Celestial Minds FZE — A Shared Commitment to Advancing Human Potential
          </h3>
          <p className="text-white/70 leading-relaxed mb-8">
            Humanity stands at a moment of profound transition.
          </p>
          <p className="text-white/60 mb-6">We envision a future where:</p>
          <ul className="space-y-3 text-left max-w-2xl mx-auto mb-8">
            {futureVisions.map((vision, i) => (
              <li key={i} className="flex items-start gap-3 text-white/70">
                <span className="text-[#c9a227] mt-1">—</span>
                <span>{vision}</span>
              </li>
            ))}
          </ul>
          <p className="text-[#c9a227] italic">
            This is the future Celestial Minds™ exists to help advance.
          </p>
        </motion.div>

        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#c9a227]/30 to-transparent mb-12" />

        {/* Why Contribution Exists */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <h3 
            className="text-xl font-light tracking-wider text-[#c9a227] mb-6"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            Why Contribution Exists
          </h3>
          <div className="space-y-4 text-white/70 leading-relaxed">
            <p>
              Celestial Minds™ was established to pursue work that is structurally important, long-horizon, and often upstream of immediate commercial viability.
            </p>
            <p>Our focus includes:</p>
            <ul className="space-y-2 ml-4">
              <li className="flex items-start gap-3">
                <span className="text-[#c9a227] mt-1">•</span>
                <span>Foundational research and systems design</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#c9a227] mt-1">•</span>
                <span>Education and capability-building frameworks</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#c9a227] mt-1">•</span>
                <span>Justice, fairness, and institutional trust tools</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#c9a227] mt-1">•</span>
                <span>Human-centric technologies and collective intelligence research</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#c9a227] mt-1">•</span>
                <span>Public-interest initiatives that markets alone do not adequately support</span>
              </li>
            </ul>
            <p className="mt-4">
              This type of work requires independence, patience, and freedom from short-term pressure. Contribution exists to protect those conditions.
            </p>
          </div>
        </motion.div>

        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#c9a227]/30 to-transparent mb-12" />

        {/* A Final Word */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <h3 
            className="text-xl font-light tracking-wider text-[#c9a227] mb-6"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            A Final Word
          </h3>
          <div className="space-y-4 text-white/70 leading-relaxed">
            <p className="text-white/90 font-medium">
              Celestial Minds™ is not seeking charity.
            </p>
            <p>
              We are inviting participation in the careful construction of a future where:
            </p>
            <ul className="space-y-2 ml-4">
              <li className="flex items-start gap-3">
                <span className="text-[#c9a227] mt-1">—</span>
                <span>Technology serves human development</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#c9a227] mt-1">—</span>
                <span>Opportunity reaches underserved communities</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#c9a227] mt-1">—</span>
                <span>Innovation is shared, not hoarded</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#c9a227] mt-1">—</span>
                <span>Freedom of mind is respected</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#c9a227] mt-1">—</span>
                <span>Societies grow through knowledge, capability, and trust</span>
              </li>
            </ul>
            <p className="mt-4">
              Some of the most consequential work must be done long before markets, products, or profits emerge.
            </p>
            <p className="text-white/90 mt-4">
              If this work resonates with you, and you wish to support its continuation, you may do so voluntarily.
            </p>
          </div>
        </motion.div>

        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#c9a227]/30 to-transparent mb-12" />

        {/* Contribute Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h3 
            className="text-2xl md:text-3xl font-light tracking-wider text-[#c9a227] mb-8"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            Contribute
          </h3>
          
          <p className="text-white/50 mb-8">
            Thank you for your consideration and support.
          </p>
          
          {/* Contribute Button */}
          <a 
            href="https://www.paypal.com/ncp/payment/GMNDCQWB4RVXY"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-10 py-4 bg-[#c9a227] text-[#050505] text-sm uppercase tracking-widest font-medium hover:bg-[#e8d5a3] transition-colors"
            data-testid="contribute-btn"
          >
            Contribute via PayPal
          </a>
          
          {/* Legal Terms */}
          <div className="mt-12 p-6 border border-[#c9a227]/20 text-left" style={{ background: 'rgba(10, 18, 37, 0.3)' }}>
            <h4 className="text-sm text-[#c9a227] mb-4 uppercase tracking-wider">Terms of Contribution</h4>
            <div className="space-y-3 text-white/50 text-xs leading-relaxed">
              <p>
                Contributions to Celestial Minds FZE are entirely voluntary and are intended solely to support independent research, conceptual development, and public-interest initiatives.
              </p>
              <p>
                Contributions do not constitute investments, do not confer equity, ownership, governance rights, or any expectation of financial return, and are non-refundable unless otherwise agreed in writing.
              </p>
              <p>
                Celestial Minds FZE does not measure contribution by amount alone. What matters is participation in shared effort, advocacy, and public-interest knowledge and systems — responsibly, transparently, and with respect for future generations.
              </p>
              <p>
                Support at any level contributes to the same foundational objective: enabling work that helps humanity thrive beyond immediate cycles, trends, or incentives.
              </p>
              <p>
                By contributing, you acknowledge and accept these terms. Contributors are responsible for compliance with applicable laws and tax obligations if any.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContributionSection;
